import dayjs from "dayjs";
import "dayjs/locale/tr";

export { logger } from "./logger";

/**
 * Format a number as Turkish Lira currency.
 */
export function formatCurrency(amount: number | string | null | undefined): string {
  return Number(amount || 0).toLocaleString("tr-TR");
}

/**
 * Sipariş kâr/zarar yardımcıları (taksit + komisyon dahil).
 *
 * Net kâr (cebimize kalan) = (gross_paid − refunded_cash) × (net_received / gross_paid)
 * Bu, iyzico komisyonunun iadeyle orantılı döndüğü modeli kullanır ve her siparişin
 * kendi efektif komisyon oranını (net_received/gross_paid) otomatik uygular.
 *
 * Nakit verisi (gross_paid/net_received) yoksa ürün-fiyatı bazına düşülür
 * (amount − refunded_amount) — eski/peşin kayıtlar için güvenli varsayılan.
 */
interface OrderFinanceInput {
  amount?: number | null;
  refunded_amount?: number | null;
  gross_paid?: number | null;
  net_received?: number | null;
  refunded_cash?: number | null;
}

const hasCashData = (o: OrderFinanceInput) =>
  Number(o.gross_paid || 0) > 0 && Number(o.net_received || 0) > 0;

/** Müşterinin ödediği brüt (nakit verisi yoksa ürün fiyatı). */
export function orderGross(o: OrderFinanceInput): number {
  return hasCashData(o) ? Number(o.gross_paid || 0) : Number(o.amount || 0);
}

/** Müşteriye iade edilen gerçek tutar (nakit verisi yoksa ürün iadesi). */
export function orderRefundCash(o: OrderFinanceInput): number {
  return hasCashData(o)
    ? Number(o.refunded_cash || 0)
    : Number(o.refunded_amount || 0);
}

/** Net kâr = cebimize kalan (komisyon ve iade düşülmüş). */
export function orderNetProfit(o: OrderFinanceInput): number {
  if (hasCashData(o)) {
    const g = Number(o.gross_paid || 0);
    const n = Number(o.net_received || 0);
    const r = Number(o.refunded_cash || 0);
    return (g - r) * (n / g);
  }
  return Number(o.amount || 0) - Number(o.refunded_amount || 0);
}

/** Bu siparişte mağazanın katlandığı gerçek iyzico komisyon maliyeti. */
export function orderCommissionCost(o: OrderFinanceInput): number {
  if (!hasCashData(o)) return 0;
  const g = Number(o.gross_paid || 0);
  const n = Number(o.net_received || 0);
  const r = Number(o.refunded_cash || 0);
  return (g - r) * (1 - n / g);
}

/**
 * Format a date string with dayjs.
 */
export function formatDate(
  date: string | null | undefined,
  format = "DD.MM.YYYY HH:mm"
): string {
  if (!date) return "-";
  return dayjs(date).format(format);
}

/**
 * Get initials from a full name for avatar fallback.
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Format file size in human-readable format.
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Validate a Turkish IBAN (TR + 24 digits).
 */
export function validateIBAN(iban: string | null | undefined): boolean {
  if (!iban) return false;
  const cleaned = iban.replace(/\s/g, "").toUpperCase();
  return /^TR\d{24}$/.test(cleaned);
}

// ── Phone (E.164) ───────────────────────────────────────────
// profiles.phone has a CHECK constraint: ^\+[1-9][0-9]{6,14}$
// (must start with "+"). Mirrors the handle_new_user DB trigger
// normalization so the CRM never produces an invalid value.
const E164_RE = /^\+[1-9]\d{6,14}$/;

/**
 * Best-effort normalization of a raw phone string to E.164.
 * Turkish numbers (0XXX / 5XXXXXXXXX) get the +90 prefix.
 * Returns null for empty input. The result may still be invalid —
 * always pair with `isValidE164` before persisting.
 */
export function normalizePhone(
  raw: string | null | undefined
): string | null {
  if (!raw) return null;
  let p = String(raw).trim().replace(/[\s\-()./]/g, "");
  if (!p) return null;
  if (p.startsWith("+")) {
    /* already prefixed */
  } else if (p.startsWith("00")) {
    p = "+" + p.slice(2);
  } else if (p.startsWith("0")) {
    p = "+90" + p.slice(1);
  } else if (/^5\d{9}$/.test(p)) {
    p = "+90" + p;
  } else {
    p = "+" + p;
  }
  return p;
}

/** True if the value is a valid E.164 phone number. */
export function isValidE164(p: string | null | undefined): boolean {
  return !!p && E164_RE.test(p);
}

/**
 * Generate a cryptographically-random, human-typeable password.
 * Avoids ambiguous characters (0/O, 1/l/I) for easy dictation.
 */
export function generatePassword(length = 14): string {
  const charset =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%*";
  const arr = new Uint32Array(length);
  crypto.getRandomValues(arr);
  let out = "";
  for (let i = 0; i < length; i++) out += charset[arr[i] % charset.length];
  return out;
}

// ── Maliye (Finance) yardımcıları ───────────────────────────
/**
 * Bir hesap perspektifinden işlemin bakiyeye işaretli etkisi.
 * Gelir +, gider −; transfer çıkan hesapta −, giren hesapta +.
 */
export function financeSignedForAccount(
  t: {
    direction: string;
    amount: number;
    account_id: string;
    transfer_to_account_id?: string | null;
  },
  accountId: string
): number {
  const amt = Number(t.amount || 0);
  if (t.direction === "income") return amt;
  if (t.direction === "expense") return -amt;
  // transfer
  if (t.transfer_to_account_id === accountId) return amt; // giren
  if (t.account_id === accountId) return -amt; // çıkan
  return 0;
}

/** Genel defterde işaretli tutar (gelir +, gider −, transfer nötr). */
export function financeSignedAmount(direction: string, amount: number): number {
  if (direction === "income") return Number(amount || 0);
  if (direction === "expense") return -Number(amount || 0);
  return 0;
}

/** KDV DAHİL bir tutarın içindeki KDV kısmını çıkarır. */
export function kdvFromGross(gross: number, rate: number): number {
  const r = Number(rate || 0) / 100;
  if (r <= 0) return 0;
  const g = Number(gross || 0);
  return g - g / (1 + r);
}

/** Ay etiketi (YYYY-MM veya tarih → "Temmuz 2026"). */
export function formatMonth(value: string): string {
  return dayjs(value).locale("tr").format("MMMM YYYY");
}

/** Telefonu tıkla-ara bağlantısına çevirir (yalnız rakam ve +). */
export const telHref = (phone: string): string => `tel:${phone.replace(/[^\d+]/g, "")}`;

/** Telefonu WhatsApp sohbet bağlantısına çevirir (yalnız rakamlar). */
export const waHref = (phone: string): string => `https://wa.me/${phone.replace(/[^\d]/g, "")}`;
